import { useEffect, useMemo, useState } from 'react';

import { isModuleInContent } from '../../utils/treeViewUtils/treeViewUtils';

/**
 * Custom hook to fetch and manage scheduled content data.
 * @param {string} serverRouteUtility - The base URL for utility services.
 * @param {Object} selectedNode - The currently selected module node.
 * @param {Object} apolloState - The global Apollo state.
 * @returns {Object} - Loading state, error state, and matched data for the selected node.
 */
export const useScheduledContent = ( serverRouteUtility, selectedNode, apolloState ) => {
  const [scheduledContent, setScheduledContent] = useState( null );
  const [isLoading, setIsLoading] = useState( false );
  const [error, setError] = useState( null );

  useEffect( () => {
    if( !serverRouteUtility ){
      return;
    }

    let isMounted = true;
    const fetchScheduledContent = async() => {
      setIsLoading( true );
      setError( null );
      try {
        const res = await fetch( `${serverRouteUtility}/devpanel?action=scheduled-content` );
        if( !res.ok ){
          throw new Error( `Failed to fetch scheduled content: ${res.status}` );
        }
        const data = await res.json();
        if( isMounted ){
          setScheduledContent( data );
        }
      }
      catch ( err ){
        if( isMounted ){
          // eslint-disable-next-line no-console
          console.error( '[useScheduledContent] Error:', err );
          setError( err.message || 'Failed to load scheduled content.' );
        }
      }
      finally {
        if( isMounted ){
          setIsLoading( false );
        }
      }
    };

    fetchScheduledContent();

    return () => {
      isMounted = false;
    };
  }, [serverRouteUtility] );

  const activeScheduledData = useMemo( () => {
    const grouped = scheduledContent?.grouped;
    if( !selectedNode ){
      return null;
    }

    // 1. Check new grouped object structure
    if( grouped ){
      // Direct match
      if( grouped[selectedNode.id] ){
        return grouped[selectedNode.id];
      }

      // Descendant match
      if( apolloState ){
        for ( const rootId in grouped ){
          const rootNode = Object.values( apolloState ).find(
            ( val ) => val && typeof val === 'object' && val.id === rootId
          );
          if( rootNode && isModuleInContent( rootNode, selectedNode.id ) ){
            return grouped[rootId];
          }
        }
      }
    }

    // 2. Fallback to old array structure
    if( Array.isArray( scheduledContent ) ){
      return scheduledContent.find( ( item ) => {
        if( item.id === selectedNode.id ){
          return true;
        }
        if( apolloState ){
          const rootNode = Object.values( apolloState ).find(
            ( val ) => val && typeof val === 'object' && val.id === item.id
          );
          if( rootNode && isModuleInContent( rootNode, selectedNode.id ) ){
            return true;
          }
        }
        return false;
      } );
    }

    return null;
  }, [scheduledContent, selectedNode, apolloState] );

  return {
    isLoading,
    error,
    activeScheduledData,
    windowWeeks: scheduledContent?.windowWeeks
  };
};
